import XIcon from '@square-icons/react/16/UI/x';
import { Entity } from '@square/ignition';
import ExpandIcon from '@squareup/dex-icons/dex/arrow/UpDownLeftRight';
import { IconButton } from '@squareup/dex-ui';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import { MarketButton } from '@squareup/dex-ui-shared-market';
import { breakHtmlForCamelCaseReact, stripV1 } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, { FunctionComponent, useCallback, useState } from 'react';

import { EntityEnum } from '../../EntityEnum';
import { PropertyContent } from '../../PropertyContent';
import { PropertyInfo } from '../../PropertyInfo';
import { useReferenceObjectModalContext } from '../../ReferenceObjectModalProvider';

import styles from './entity-details.module.css';

type EntityDetailsProps = {
  entity: Entity;
  onClose: () => void;
  depth: number;
  narrowView?: boolean;
  entityStack?: Entity[];
};

const EntityDetails: FunctionComponent<EntityDetailsProps> = ({
  entity,
  depth,
  onClose,
  narrowView = false,
  entityStack = [],
}) => {
  const referenceObjectModalContext = useReferenceObjectModalContext();
  const openModal = useCallback(() => {
    referenceObjectModalContext.open(entity, entityStack);
  }, [entity, entityStack, referenceObjectModalContext]);

  const [isBehind, setIsBehind] = useState(false);

  const createTable = () => {
    if (entity.isEnum) {
      return entity.enums
        .filter((entityEnum) => entityEnum.releaseStatus !== 'RETIRED') // Filter out retired enums
        .map((enumValue, i, list) => {
          const border =
            i !== list.length - 1
              ? { line: { bottom: 'standard' as 'standard' } }
              : undefined;

          return (
            <Box as="tr" key={enumValue.value} className={styles.block}>
              <Box padding={{ bottom: '2x', top: '2x' }} border={border}>
                <EntityEnum
                  entityEnum={enumValue}
                  disableAnchor={true}
                ></EntityEnum>
              </Box>
            </Box>
          );
        });
    } else {
      return entity.properties
        .filter((property) => !property.isRetired) // Filter out retired properties
        .map((property, i, list) => {
          return (
            <Box
              as="tr"
              key={property.name}
              className={styles.block}
              data-testid={`entity-property-${property.name}`}
            >
              <Box
                padding={{ bottom: '2x', top: '2x' }}
                border={
                  i !== list.length - 1 ? { line: { bottom: '20' } } : undefined
                }
              >
                <PropertyInfo
                  category={null}
                  property={property}
                  disableAnchor={true}
                  narrowView={narrowView}
                  entityStack={[...entityStack, entity]}
                ></PropertyInfo>
                <PropertyContent
                  property={property}
                  depth={depth}
                  onShowingDetailsChange={setIsBehind}
                  narrowView={narrowView}
                  entityStack={[...entityStack, entity]}
                ></PropertyContent>
              </Box>
            </Box>
          );
        });
    }
  };

  return (
    <Box
      border="standard"
      className={clsx(styles['container'], isBehind && styles['behind'])}
    >
      <Box
        padding={{ bottom: '1x', left: '1x', right: '1x', top: '1x' }}
        border={{ line: { bottom: 'standard' } }}
        className={clsx(
          styles['header'],
          narrowView && styles['header-narrow']
        )}
      >
        <IconButton
          aria-label={`Close attributes view for ${entity.name}`}
          testId={`close-entity-details-${entity.name}`}
          trackingId="close-entity-details"
          onClick={onClose}
          className={clsx(styles.tertiary)}
          icon={
            <XIcon
              className={clsx(
                commonIconStyles['icon-fill-current-color'],
                styles['icon-button']
              )}
              slot="icon"
            ></XIcon>
          }
        ></IconButton>
        <Paragraph20
          weight="medium"
          margin={narrowView ? { left: '0.5x' } : undefined}
        >
          {breakHtmlForCamelCaseReact(stripV1(entity.name))}
        </Paragraph20>
        <MarketButton
          aria-label={`Open details modal for ${entity.name}`}
          testId={`open-entity-details-modal-${entity.name}`}
          trackingId="open-entity-details-modal"
          rank="secondary"
          size="small"
          onClick={openModal}
          className={clsx(narrowView && styles['header-item-end'])}
        >
          <ExpandIcon slot="icon" className={commonIconStyles['icon-color']} />
        </MarketButton>
      </Box>
      <Box
        as="table"
        padding={{ left: '2x', right: '2x' }}
        className={styles.block}
      >
        <tbody
          className={clsx(
            styles.block,
            entity.isEnum && depth > 1 && styles['enum-body']
          )}
        >
          {createTable()}
        </tbody>
      </Box>
    </Box>
  );
};

export { EntityDetails };
